// packages/web/src/layouts/AssessmentShell.tsx
/**
 * AssessmentShell
 * Unified layout for all assessment phases: evidence, questions, report.
 * Renders: breadcrumb -> PhaseBar -> sidebar (phase-specific) + content.
 * InvestorReportLayout is NOT affected — investors don't see the PhaseBar.
 */

import { Outlet, useParams, useLocation, Link } from "react-router-dom";
import { useAssessment } from "@/hooks/useAssessment";
import { useTenant } from "@/contexts/TenantContext";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { PhaseBar } from "@/components/assessment/PhaseBar";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { getAssessmentTitle } from "@/lib/assessment";
import { AssessmentWorkflowState } from "@app/types";
import { ReportProvider } from "@/contexts/ReportContext";

type AssessmentPhase = "evidence" | "questions" | "report";

/**
 * Determines the current assessment phase from the URL pathname.
 *
 * @param pathname - The current URL pathname
 * @param assessmentId - The assessment ID used to construct route prefixes
 * @returns The detected phase: 'report', 'questions', or 'evidence'
 */
function detectPhase(pathname: string, assessmentId: string): AssessmentPhase {
  const base = `/assessments/${assessmentId}`;
  if (pathname.startsWith(`${base}/analysis`)) return "report";
  if (pathname.startsWith(`${base}/questions`)) return "questions";
  return "evidence";
}

/**
 * Unified layout shell for all assessment phases (evidence, questions, report).
 * Provides a persistent breadcrumb and PhaseBar across phases, delegates sidebar
 * and content rendering to child routes via Outlet. Wraps report-phase routes
 * with ReportProvider automatically.
 *
 * @returns The assessment shell layout JSX element
 */
export function AssessmentShell(): JSX.Element {
  const { id: assessmentId } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const { currentTenant, isLoading: isLoadingTenant } = useTenant();
  const { assessment, isLoading, error } = useAssessment(assessmentId!);

  if (!assessmentId) {
    return (
      <div className="p-8 text-center text-destructive">
        Missing assessment ID.
      </div>
    );
  }

  if (isLoading || isLoadingTenant) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" label="Loading assessment..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <p className="text-sm text-destructive">
          Failed to load assessment: {error.message}
        </p>
        <Link
          to="/assessments"
          className="text-sm text-primary hover:underline"
        >
          Return to assessments
        </Link>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" label="Loading assessment..." />
      </div>
    );
  }

  if (!currentTenant) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <p className="text-sm text-destructive">No organization selected.</p>
        <Link
          to="/assessments"
          className="text-sm text-primary hover:underline"
        >
          Return to assessments
        </Link>
      </div>
    );
  }

  const title = getAssessmentTitle(assessment);
  const phase = detectPhase(pathname, assessmentId);
  const workflowState = assessment.workflowState as AssessmentWorkflowState;

  const content = (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Breadcrumb */}
      <div className="border-b border-border px-6 py-3 shrink-0">
        <Breadcrumb assessmentName={title} />
      </div>

      {/* Phase Bar */}
      <div className="shrink-0">
        <PhaseBar currentState={workflowState} assessmentId={assessmentId} />
      </div>

      {/* Sidebar + Content — child routes render via Outlet */}
      {/* Note: child pages call useAssessment() independently. This is a
          minor double-fetch but avoids coupling every child to useOutletContext.
          React Query deduplicates the request so there's no extra network call. */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <Outlet />
      </div>
    </div>
  );

  // Wrap report phase with ReportProvider
  if (phase === "report") {
    const basePath = `/assessments/${assessmentId}/analysis`;
    return (
      <ReportProvider
        value={{
          tenantId: currentTenant.id,
          assessmentId,
          basePath,
          isInvestorView: false,
          hookOptions: undefined,
        }}
      >
        {content}
      </ReportProvider>
    );
  }

  return content;
}
